import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * FilterableHeader — A table header cell that doubles as a dropdown filter.
 *
 * Props:
 *   label:       default column label (e.g. "Pharmacie")
 *   allLabel:    text shown when no filter is active (e.g. "TOUS", "TOUTES")
 *   values:      array of unique string values to pick from
 *   selected:    currently selected value (string) or null for "all"
 *   onSelect:    (value: string | null) => void
 *   sortable:    boolean — show sort arrow
 *   sortDir:     'asc' | 'desc' | null
 *   onSort:      () => void
 *   className:   extra classes for the <th>
 */
const FilterableHeader = ({ label, allLabel, values, selected, onSelect, sortable, sortDir, onSort, formatValue = (v) => v, className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayLabel = selected ? `${label}: ${formatValue(selected)}` : label;
  const isFiltered = !!selected;
  const hasDropdown = values && values.length > 0;

  return (
    <th ref={ref} className={`text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase relative select-none ${className}`}>
      <div className="flex items-center gap-1">
        {/* Sort button */}
        {sortable && (
          <button type="button" onClick={onSort} className="hover:text-gray-700 transition-colors">
            {label}
            <span className="ml-0.5 text-gray-400">{sortDir === 'asc' ? '↑' : sortDir === 'desc' ? '↓' : '↕'}</span>
          </button>
        )}

        {/* Filter dropdown trigger */}
        {hasDropdown ? (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className={`flex items-center gap-1 transition-colors whitespace-nowrap ${
              isFiltered ? 'text-blue-600 font-bold' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {!sortable && displayLabel}
            <ChevronDown size={12} className={`transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
          </button>
        ) : (
          !sortable && <span>{label}</span>
        )}
      </div>

      {/* Dropdown */}
      {open && hasDropdown && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl min-w-[160px] max-h-64 overflow-y-auto">
          {/* "Tous" option */}
          <button
            type="button"
            onClick={() => { onSelect(null); setOpen(false); }}
            className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
              !selected ? 'text-blue-600 font-medium' : 'text-gray-700'
            }`}
          >
            {!selected && <Check size={14} className="text-blue-600" />}
            {!selected ? '' : <span className="w-3.5" />}
            {allLabel || 'Tous'}
          </button>
          <div className="border-t border-gray-100" />
          {values.map(val => (
            <button
              key={val}
              type="button"
              onClick={() => { onSelect(val); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
                selected === val ? 'text-blue-600 font-medium' : 'text-gray-700'
              }`}
            >
              {selected === val ? <Check size={14} className="text-blue-600" /> : <span className="w-3.5" />}
              {formatValue(val)}
            </button>
          ))}
        </div>
      )}
    </th>
  );
};

/**
 * useTableFilters — Hook to manage filter state for a table.
 *
 * Usage:
 *   const { filters, setFilter, getUniqueValues, applyFilters } = useTableFilters(data, filterConfig);
 *
 * filterConfig: [{ key: 'pharmacy_name', accessor: (row) => row.pharmacy_name || '—' }]
 */
const useTableFilters = (data, filterConfig) => {
  const [filters, setFilters] = useState({});

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getUniqueValues = useMemo(() => {
    const map = {};
    filterConfig.forEach(col => {
      const vals = new Set();
      data.forEach(row => {
        const v = col.accessor(row);
        if (v != null && v !== '' && v !== '—') vals.add(String(v));
      });
      map[col.key] = Array.from(vals).sort((a, b) => a.localeCompare(b, 'fr'));
    });
    return map;
  }, [data, filterConfig]);

  const applyFilters = useMemo(() => {
    const active = Object.entries(filters).filter(([, v]) => v != null);
    if (active.length === 0) return data;
    return data.filter(row => {
      return active.every(([key, val]) => {
        const col = filterConfig.find(c => c.key === key);
        if (!col) return true;
        return String(col.accessor(row)) === val;
      });
    });
  }, [data, filters, filterConfig]);

  return { filters, setFilter, getUniqueValues, filteredData: applyFilters };
};

const SortableHeader = ({ label, sortKey, currentSort, onSort, className = "" }) => {
  const isSorted = currentSort?.key === sortKey;
  const direction = currentSort?.direction;

  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase cursor-pointer select-none hover:text-gray-900 transition-colors whitespace-nowrap ${className}`}
    >
      <div className="inline-flex items-center gap-1">
        <span>{label}</span>
        {isSorted ? (
          direction === 'desc' ? (
            <ArrowDown className="w-3.5 h-3.5 text-blue-600 font-bold" />
          ) : (
            <ArrowUp className="w-3.5 h-3.5 text-blue-600 font-bold" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 text-gray-300 hover:text-gray-500" />
        )}
      </div>
    </th>
  );
};

export { FilterableHeader, SortableHeader, useTableFilters };
export default FilterableHeader;
